import type { Transaction } from './tx';

// Models (mirror docs/API.md of COINWOW/coinwow-api; amounts in satoshis).

export interface APIStatus {
  api_version: string;
  chain: string;
  node_height: number;
  indexed_height: number;
  synced: boolean;
  mempool_size: number;
}

export interface APIFees {
  unit: string;
  fast: number;
  normal: number;
  slow: number;
  minimum: number;
  source: string;
}

export interface APIAddressSummary {
  address: string;
  confirmed: number;
  unconfirmed: number;
  total: number;
  tx_count: number;
  mempool_tx_count: number;
}

export interface APIWalletSummary {
  height: number;
  totals: APIAddressSummary;
  addresses: APIAddressSummary[];
}

export interface APIUTXO {
  txid: string;
  vout: number;
  address: string;
  value: number;
  script_pubkey: string;
  height: number | null;
  confirmations: number;
  coinbase: boolean;
  spendable: boolean;
}

export interface APIHistoryItem {
  txid: string;
  height: number | null;
  confirmations: number;
  time: number;
  received: number;
  sent: number;
  net: number;
  fee: number | null;
}

export interface APIHistoryPage {
  height: number;
  txs: APIHistoryItem[];
  next_cursor: string | null;
}

export const isUsed = (s: APIAddressSummary) => s.tx_count + s.mempool_tx_count > 0;
export const isPending = (h: APIHistoryItem) => h.height === null;

export type APIErrorKind =
  | { kind: 'rejected'; message: string }
  | { kind: 'rateLimited' }
  | { kind: 'invalidAddress'; message: string }
  | { kind: 'server'; status: number; message: string }
  | { kind: 'invalidResponse' }
  | { kind: 'offline'; detail: string };

export class APIError extends Error {
  constructor(public readonly reason: APIErrorKind) {
    super(reason.kind);
  }
}

/**
 * Chooses which COINWOW server to use. All servers are equivalent: each app
 * picks one at random (load sharing) and stays on it while it works. A server
 * that fails is skipped for a minute and the request is retried on the next.
 */
export class ServerPool {
  private current: number;
  private downUntil = new Map<number, number>();
  static retryAfterMs = 60_000;

  constructor(readonly servers: string[], shuffle = true) {
    if (servers.length === 0) throw new Error('no servers');
    this.current = shuffle ? Math.floor(Math.random() * servers.length) : 0;
  }

  order(): number[] {
    const now = Date.now();
    const all = this.servers.map((_, i) => (this.current + i) % this.servers.length);
    const up = all.filter((i) => (this.downUntil.get(i) ?? 0) <= now);
    return [...up, ...all.filter((i) => !up.includes(i))];
  }

  succeeded(i: number) {
    this.downUntil.delete(i);
    this.current = i;
  }

  failed(i: number) {
    this.downUntil.set(i, Date.now() + ServerPool.retryAfterMs);
  }

  get currentHost(): string {
    try {
      return new URL(this.servers[this.current]).host;
    } catch {
      return this.servers[this.current];
    }
  }
}

export const PRODUCTION_SERVERS = ['https://wallet-api.coinwow.ca', 'https://wallet-api2.coinwow.ca'];

/**
 * Anonymous "app open right now" marker for the live users counter: a random
 * value drawn at each page load, kept in memory only (never stored, never tied
 * to the wallet). The servers keep it 5 minutes in memory, then forget it.
 * Sent as query parameters: no CORS preflight needed.
 */
const LIVE_SESSION = (() => {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
})();

export function withLiveParams(path: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}cw_client=web&cw_session=${LIVE_SESSION}`;
}

/**
 * Client for the public, non-custodial COINWOW API. It only ever sends
 * addresses and already-signed transactions: never keys or phrases.
 */
export class APIClient {
  readonly pool: ServerPool;

  constructor(servers: string[] = PRODUCTION_SERVERS, private readonly fetchImpl: typeof fetch = (...a) => fetch(...a)) {
    this.pool = new ServerPool(servers);
  }

  /**
   * Sends to the current server, and to the next one if that server is down,
   * overloaded or broken (network error, 5xx, 429). Answers about the request
   * itself (400, 404…) are returned as-is. Re-sending a signed transaction is
   * harmless: nodes accept the same transaction only once.
   */
  async send<T>(method: 'GET' | 'POST', path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    let last: APIError = new APIError({ kind: 'invalidResponse' });
    for (const i of this.pool.order()) {
      try {
        const r = await this.sendTo<T>(this.pool.servers[i], method, path, body, signal);
        this.pool.succeeded(i);
        return r;
      } catch (e) {
        if (!(e instanceof APIError)) throw e;
        const k = e.reason.kind;
        if (k === 'offline' || k === 'rateLimited' || k === 'invalidResponse' || (k === 'server' && e.reason.status >= 500)) {
          this.pool.failed(i);
          last = e;
          continue;
        }
        this.pool.succeeded(i);
        throw e;
      }
    }
    throw last;
  }

  private async sendTo<T>(base: string, method: string, path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const timeout = AbortSignal.timeout(15_000);
    const combined = signal ? anySignal([signal, timeout]) : timeout;
    let res: Response;
    try {
      res = await this.fetchImpl(`${base}/${withLiveParams(path)}`, {
        method,
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: combined,
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      const host = new URL(base).host;
      throw new APIError({ kind: 'offline', detail: `${host} ${method} /${path}` });
    }
    let json: any = null;
    try {
      json = await res.json();
    } catch {
      if (res.ok) throw new APIError({ kind: 'invalidResponse' });
    }
    if (!res.ok) {
      const kind = json?.error;
      const message = typeof json?.message === 'string' ? json.message : '';
      if (res.status === 429) throw new APIError({ kind: 'rateLimited' });
      if (res.status === 400 && kind === 'rejected') throw new APIError({ kind: 'rejected', message });
      if (res.status === 400 && kind === 'invalid_address') throw new APIError({ kind: 'invalidAddress', message });
      throw new APIError({ kind: 'server', status: res.status, message });
    }
    if (json === null || typeof json !== 'object') throw new APIError({ kind: 'invalidResponse' });
    return json as T;
  }

  status() {
    return this.send<APIStatus>('GET', 'v1/status');
  }
  fees() {
    return this.send<APIFees>('GET', 'v1/fees');
  }
  summary(addresses: string[]) {
    return this.send<APIWalletSummary>('POST', 'v1/wallet/summary', { addresses });
  }
  async utxos(addresses: string[]): Promise<APIUTXO[]> {
    const r = await this.send<{ height: number; utxos: APIUTXO[] }>('POST', 'v1/wallet/utxos', { addresses });
    if (!Array.isArray(r.utxos)) throw new APIError({ kind: 'invalidResponse' });
    return r.utxos;
  }
  history(addresses: string[], cursor: string | null = null, limit = 25) {
    return this.send<APIHistoryPage>('POST', 'v1/wallet/txs', { addresses, cursor: cursor ?? '', limit });
  }
  /** Relays a signed transaction. Returns its txid. */
  async broadcast(tx: Transaction): Promise<string> {
    const r = await this.send<{ txid: string }>('POST', 'v1/tx', { hex: tx.hex });
    if (r.txid !== tx.txid) throw new APIError({ kind: 'invalidResponse' });
    return r.txid;
  }
}

function anySignal(signals: AbortSignal[]): AbortSignal {
  const any = (AbortSignal as any).any as ((s: AbortSignal[]) => AbortSignal) | undefined;
  if (any) return any(signals);
  const c = new AbortController();
  for (const s of signals) {
    if (s.aborted) c.abort();
    s.addEventListener('abort', () => c.abort(), { once: true });
  }
  return c.signal;
}
